import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

interface Auth {
  session: Session | null;
  laddar: boolean;
  aktiverad: boolean;
  loggaIn: (epost: string, losenord: string) => Promise<string | null>;
  skickaLank: (epost: string) => Promise<string | null>;
  loggaUt: () => Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

// Felmeddelanden på svenska. Supabase skickar engelska texter.
function felText(m: string): string {
  if (/invalid login/i.test(m)) return "Fel e-postadress eller lösenord.";
  if (/email not confirmed/i.test(m)) return "E-postadressen är inte bekräftad än. Leta efter bekräftelsemejlet i inkorgen.";
  if (/rate limit/i.test(m)) return "För många försök. Vänta en stund och försök igen.";
  if (/failed to fetch|network/i.test(m)) return "Kunde inte nå inloggningen. Kontrollera internetanslutningen och försök igen.";
  return "Det gick inte att logga in: " + m;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [laddar, setLaddar] = useState(!!supabase);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setLaddar(false); });
    const { data } = supabase.auth.onAuthStateChange((_h, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);

  const auth: Auth = {
    session, laddar, aktiverad: !!supabase,
    loggaIn: async (email, password) => {
      if (!supabase) return "Inloggningen är inte påslagen än.";
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return error ? felText(error.message) : null;
    },
    skickaLank: async (email) => {
      if (!supabase) return "Inloggningen är inte påslagen än.";
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false, emailRedirectTo: window.location.origin + "/app" } });
      return error ? felText(error.message) : null;
    },
    loggaUt: async () => { await supabase?.auth.signOut(); },
  };
  return <Ctx.Provider value={auth}>{children}</Ctx.Provider>;
}

export function useAuth(): Auth {
  const a = useContext(Ctx);
  if (!a) throw new Error("useAuth utanför AuthProvider");
  return a;
}
